import { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { Modal } from 'antd'
import { compact, uniq } from 'lodash/fp'

import { Button } from 'components/Button/Button'
import { CloseButton } from 'components/Buttons/CloseButton'
import { InheritAccessMessage, WorkflowStatusMessage } from './ConfirmationModalMessages'
import ConfirmationModalStore from 'stores/ConfirmationModalStore'
import { useFolder } from 'hooks/queries/folders/useFolder'
import { useWorkflow } from 'hooks/queries/workflow/useWorkflow'

import Styles from './ActionModal.module.scss'

export const ConfirmationModal = observer(() => {
  const store = ConfirmationModalStore

  const workflowQuery = useWorkflow({ folderId: store.destinationKey })
  const workflowDefaultProperty = workflowQuery.data?.workflow?.property?.property_values?.[0]
  const { data: parentFolderData } = useFolder(store.parentId || '')
  const parentTitle = parentFolderData?.folder?.title

  useEffect(() => {
    if (parentTitle) {
      store.setState({ currentParentTitle: parentTitle })
    }
  }, [parentTitle, store])

  if (!store.movingToRoot && workflowQuery.isLoading) return null

  const parentsCount = uniq(
    compact(
      store.selectedFiles.map((file) => {
        return file?.parent_id
      }),
    ),
  ).length

  const topLevelConfirmationText = (
    <>
      <>
        Your selections will be first-level folders without inherited access.{' '}
        <InheritAccessMessage parentsCount={parentsCount} currentParentTitle={store.currentParentTitle} />
      </>
      <ul className={Styles.listWithBullets}>
        <li>The status will be removed.</li>
        <li>Open requests will be ended.</li>
        {store.differentWorkspace && <li>All copies will be unlinked and references removed.</li>}
      </ul>
    </>
  )

  const folderConfirmationText = (
    <>
      Everyone with access to <b>{store.destinationTitle}</b>
      {store.differentWorkspace && ` in ${store.workspaceName}`} will have access.{' '}
      {!store.destinationIsAChild && (
        <InheritAccessMessage parentsCount={parentsCount} currentParentTitle={store.currentParentTitle} />
      )}
      <ul className={Styles.listWithBullets}>
        {store.selectedFolder && workflowDefaultProperty && (
          <li>
            <WorkflowStatusMessage workflowDefaultProperty={workflowDefaultProperty} />
          </li>
        )}
        <li>Open requests will be ended.</li>
        {store.differentWorkspace && <li>All copies will be unlinked and references removed.</li>}
      </ul>
    </>
  )

  return (
    <Modal
      visible={store.isConfirmationModalOpen}
      onCancel={store.closeModal}
      wrapClassName={Styles.actionModalContainer}
      footer={null}
      closable={false}
      width={560}
    >
      <div className={Styles.confirmation}>
        <div className={Styles.header}>
          <span className={Styles.headerText}>Change access and status?</span>
          <CloseButton onClick={() => store.closeModal()} />
        </div>
        <div className={Styles.confirmationBody}>
          <div className={Styles.confirmationText}>
            {store.movingToRoot ? topLevelConfirmationText : folderConfirmationText}
          </div>
        </div>
        <div className={Styles.confirmationFooter}>
          <Button size="xl" variant="tertiary" className={Styles.cancelButton} onPress={() => store.closeModal()}>
            Cancel
          </Button>
          <Button
            onPress={() => {
              store.closeModal()

              const isFolderMissing = !store.selectedFolder && !store.movingToRoot
              if (!store.newWorkspaceId || isFolderMissing) return

              store.submit({ newWorkspaceId: store.newWorkspaceId, selectedFolder: store.selectedFolder })
            }}
            size="xl"
          >
            Confirm
          </Button>
        </div>
      </div>
    </Modal>
  )
})
